<script setup lang="ts">
import RlUploadPdf from '@/components/common/upload/RlUploadPdf.vue'
import { createRepairRequest, getRepairRequest, updateRepairRequest } from '@/services/rc.service'
import { useRepairActionStore } from '@/stores/repair-action.store'
import { useRepairServiceStore } from '@/stores/repair-service.store'
import type {
  CreateRepairRequestForm,
  CustomRepairFeeItem,
  ScopeOfWorkService,
} from '@/types/rc/repair-requests/CreateRepairRequest'
import { RepairRequestTypes } from '@/types/rc/repair-requests/CreateRepairRequest'
import type { IRepairAction } from '@/types/rc/miners'
import type { IService } from '@/types/rc/work-orders'
import { Delete } from '@element-plus/icons-vue'
import { Icon } from '@iconify/vue'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { ElLoading, ElMessage, type FormInstance, type FormRules } from 'element-plus'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types'
import { DateTime } from 'luxon'
import { storeToRefs } from 'pinia'
import { computed, reactive, ref } from 'vue'
import { useMinerModelFamilies } from '@/composables/core/miner-model-families'

const repairServiceStore = useRepairServiceStore()
const { repairServices } = storeToRefs(repairServiceStore)
const repairServiceMap = computed<Map<number, IService>>(
  () => new Map(repairServices.value.map((svc) => [svc.id, svc]))
)

const repairActionStore = useRepairActionStore()
const { repairActions } = storeToRefs(repairActionStore)
const repairActionsMap = computed<Map<number, IRepairAction>>(
  () => new Map(repairActions.value.map((act) => [act.id, act]))
)

const props = defineProps<{
  customerId: number
  repairRequestId?: string
}>()

const emit = defineEmits(['close'])

// Options
const { minerModelFamilyOptions } = useMinerModelFamilies()

// Flags
const visible = ref<boolean>(false)
const formSubmitLoading = ref<boolean>(false)

// Data
const repairActionOptions = ref<OptionType[]>([])
const repairServiceOptions = ref<OptionType[]>([])
const staticRepairServiceOptions = ref<OptionType[]>([])

// DOM refs
const dialogRef = ref()
const formRef = ref<FormInstance>()
const uploadRef = ref()

// Form
const customRepairFees = ref<boolean>(false)
const addingNewRSR = ref<boolean>(false)
const initialFiles = ref<any[]>([])
const rsrsMarkedForDeletion = ref<number[]>([])

// Errors
const alert = ref<{ title: string; details: string[] } | null>(null)

// see https://element-plus.org/en-US/component/form.html#validation
const validationRules = reactive<FormRules<CreateRepairRequestForm>>({
  request_date: [{ required: true, message: 'Please select a request date.' }],
  expected_units: [{ required: true, message: 'Please specify total units.' }],
  scope_of_work: [{ required: true, message: 'Please select at least one service.' }],
  miner_models: [{ required: true, message: 'Please select at least one miner model.' }],
  signed_document: [{ required: true, message: 'Signed document is required.' }],
})

const form = reactive<CreateRepairRequestForm>({
  customer_id: props.customerId,
  type: RepairRequestTypes.RSR,
  request_date: new Date(),
  expected_units: undefined,
  scope_of_work: Array<ScopeOfWorkService>(),
  miner_models: Array<string>(),
  repair_fees: Array<CustomRepairFeeItem>(),
  signed_document: undefined,
})

/**
 * Disables future dates in the date picker.
 */
const disabledDate = (time: Date) => {
  return time.getTime() > Date.now()
}

/**
 * Updates available custom repair fee options based on form state.
 */
const updateAvailableRepairFeeOptions = (repairActionID?: number) => {
  const selectedOptions = form.repair_fees.map((fee) => fee.repair_action_id)
  repairActionOptions.value.forEach((option) => (option.disabled = selectedOptions.includes(option.value)))
  repairActionOptions.value.sort((a, b) => {
    if (a.disabled && !b.disabled) {
      return 1
    } else if (b.disabled && !a.disabled) {
      return -1
    }

    if (a.value > b.value) {
      return 1
    } else if (b.value > a.value) {
      return -1
    }

    return 0
  })

  // If the repairActionID is not null, find it and assign it the repair action base fee
  if (!repairActionID) return

  const newActionData = form.repair_fees.find((fee) => fee.repair_action_id == repairActionID.toString())
  if (newActionData) {
    newActionData.price = repairActionsMap.value.get(repairActionID)?.price
  }
}

/**
 * Updates available service options based on form state.
 */
const updateAvailableServiceOptions = (serviceID?: number) => {
  const selectedOptions = form.scope_of_work.map((svc) => svc.service_id)
  repairServiceOptions.value.forEach((option) => (option.disabled = selectedOptions.includes(option.value)))
  repairServiceOptions.value.sort((a, b) => {
    if (a.disabled && !b.disabled) {
      return 1
    } else if (b.disabled && !a.disabled) {
      return -1
    }

    if (a.value > b.value) {
      return 1
    } else if (b.value > a.value) {
      return -1
    }

    return 0
  })

  // If the serviceID is not null, find it and assign it the service base fee
  if (!serviceID) return

  const newServiceData = form.scope_of_work.find((svc) => svc.service_id == serviceID.toString())
  if (newServiceData) {
    newServiceData.price = repairServiceMap.value.get(serviceID)?.price
  }
}

/**
 * Adds a custom repair fee to the form.
 */
const addCustomRepairFee = () => {
  form.repair_fees.push({
    key: Date.now(),
    repair_action_id: null,
    price: undefined,
  })
}

/**
 * Adds a custom repair fee to the form.
 */
const addService = () => {
  form.scope_of_work.push({
    key: Date.now(),
    service_id: null,
    price: undefined,
    included_service_ids: undefined,
  })
}

/**
 * Removes a custom repair fee from the form.
 *
 * @param item the custom fee item to remove.
 */
const removeCustomRepairFee = (item: CustomRepairFeeItem) => {
  const index = form.repair_fees.indexOf(item)
  if (index !== -1) {
    form.repair_fees.splice(index, 1)
    updateAvailableRepairFeeOptions()
  }
}

/**
 * Removes a service from the form.
 *
 * @param item the service to remove.
 */
const removeService = (item: ScopeOfWorkService) => {
  const index = form.scope_of_work.indexOf(item)
  if (index !== -1) {
    form.scope_of_work.splice(index, 1)
    updateAvailableServiceOptions()
  }
}

/**
 * Marks an RSR for deletion (will be deleted once the user saves their changes).
 */
const markRSRForDeletion = (fileId: number) => {
  const index = initialFiles.value.findIndex((file) => file.id == fileId)
  initialFiles.value.splice(index, 1)
  rsrsMarkedForDeletion.value.push(fileId)
}

/**
 * Handles "Add new RSR document" "Cancel" button click.
 * Clears form documents and revalidates field.
 */
const handleCancelAddingNewDocuments = async () => {
  addingNewRSR.value = false
  form.signed_document = undefined
  await formRef.value?.validateField('signed_documents', () => undefined)
}

/**
 * Resets the form (if it exists), clears upload's file list, and resets custom repair fees.
 *
 * @param formEl the form instance to reset.
 */
const resetForm = (formEl: FormInstance | undefined) => {
  if (!formEl) return

  formEl.resetFields()
  form.scope_of_work = []
  form.repair_fees = []
  customRepairFees.value = false

  formSubmitLoading.value = false
}

/**
 * Handles dialog close: clears alert, resets form, clears upload file list.
 * Update-only: resets "adding new RSR" flag, clears RSR's marked for deletion, clears initial files.
 *
 * @emits close alerts parent component that the dialog has been closed.
 */
const handleDialogClose = () => {
  // Edit document flags
  addingNewRSR.value = false
  rsrsMarkedForDeletion.value = []

  // Reset initial files and upload
  initialFiles.value = []
  uploadRef.value?.clear()

  // Reset alert and form
  alert.value = null
  resetForm(formRef.value)

  emit('close')
}

/**
 * Validates the form (if it exists), then submits if valid.
 * @param formEl the form instance to validate and submit.
 */
const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return
  alert.value = null
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  await formEl.validate(async (valid, fields) => {
    if (valid) {
      try {
        formSubmitLoading.value = true
        if (!customRepairFees.value) {
          form.repair_fees = []
        }
        if (props.repairRequestId) {
          await update.mutateAsync({
            id: props.repairRequestId,
            variables: { form, docsMarkedForDeletion: rsrsMarkedForDeletion.value },
          })
          rsrsMarkedForDeletion.value = []
        } else {
          await create.mutateAsync(form)
        }
        visible.value = false
        ElMessage({
          type: 'success',
          message: `Successfully ${props.repairRequestId ? 'updated' : 'created'} repair request`,
        })
      } catch (err: any) {
        alert.value = {
          title: err.description,
          details: err.details,
        }
      } finally {
        formSubmitLoading.value = false
      }
    }
  })
}

/**
 * Populates RC services.
 */
const populateServices = async () => {
  repairServiceOptions.value = repairServices.value.map((svc: IService) => {
    return {
      label: svc.description,
      value: svc.id,
    }
  })
  staticRepairServiceOptions.value = repairServices.value.map((svc: IService) => {
    return {
      label: svc.description,
      value: svc.id,
    }
  })
}

/**
 * Populates repair actions select.
 */
const populateRepairActions = async () => {
  repairActionOptions.value = repairActions.value.map((item: IRepairAction) => {
    return {
      label: `${item.part_type} - ${item.action}`,
      value: item.id,
    }
  })
}

/**
 * Initializes form: populates services, repair actions (for custom fees),
 * if the form is in update mode (a repair request ID prop was passed),
 * loads initial form data.
 */
const initForm = async () => {
  // Dialog loading state workaround, see:
  // https://github.com/element-plus/element-plus/issues/6706#issuecomment-1780400671
  const loading = ElLoading.service({
    target: dialogRef.value.$el.nextElementSibling.querySelector('.el-dialog'),
  })

  try {
    await populateRepairActions()
    await populateServices()
    addCustomRepairFee()
    if (props.repairRequestId) {
      try {
        const data = await getRepairRequest(props.repairRequestId)
        form.request_date = DateTime.fromISO(data.request_date).toJSDate()
        form.expected_units = data.expected_units
        form.scope_of_work = data.scope_of_work.map((i: any) => i.id)
        form.miner_models = data.miner_models
        if (data.custom_repair_fees.length > 0) {
          customRepairFees.value = true
          data.custom_repair_fees.forEach((fee: any, index: number) => {
            if (index > 0) {
              addCustomRepairFee()
            }
            form.repair_fees[index] = {
              repair_action_id: fee.repair_action.id,
              price: Number(fee.price),
              key: index,
            }
          })
          updateAvailableRepairFeeOptions()
        }
        if (data.scope_of_work.length > 0) {
          data.scope_of_work.forEach((svc: any, index: number) => {
            form.scope_of_work[index] = {
              included_service_ids: svc.included_services.map((i: any) => i.id),
              service_id: svc.service.id,
              price: Number(svc.price),
              key: index,
            }
          })
          updateAvailableServiceOptions()
        }
        if (data.documents.length > 0) {
          data.documents.forEach((document: any) => {
            initialFiles.value.push(document)
          })
        }
      } catch {
        ElMessage({
          type: 'error',
          message: `Failed to load repair request "${props.repairRequestId}"`,
        })
      }
    }
  } catch (err: any) {
    ElMessage({
      type: 'error',
      message: err.message,
    })
    visible.value = false
  } finally {
    loading.close()
  }
}

// Vue Query
const queryClient = useQueryClient()

// Create Repair Request
const create = useMutation({
  mutationFn: (form: any) => createRepairRequest(form),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['repair-requests'] })
  },
})

// Update Repair Request
const update = useMutation({
  mutationFn: ({ id, variables }: { id: string; variables: { form: any; docsMarkedForDeletion: any } }) =>
    updateRepairRequest(id, variables.form, variables.docsMarkedForDeletion),
  onSuccess: (data, variables) => {
    queryClient.setQueryData(['repair-request-details', variables.id], data)
  },
})

// Expose visible ref for parent interaction.
defineExpose({
  visible,
})
</script>

<template>
  <el-dialog
    ref="dialogRef"
    v-model="visible"
    :width="824"
    :title="`${repairRequestId ? 'Update' : 'Create'} Repair Request`"
    :show-close="false"
    @open="initForm"
    @closed="handleDialogClose"
  >
    <el-form
      ref="formRef"
      :model="form"
      :rules="validationRules"
      :hide-required-asterisk="true"
      label-position="left"
      :label-width="192"
    >
      <el-form-item v-if="repairRequestId" label="Repair Request">
        <CopyButton :value="repairRequestId"> #{{ repairRequestId.slice(-4) }} </CopyButton>
      </el-form-item>
      <el-form-item label="Request date" prop="request_date">
        <el-date-picker v-if="!repairRequestId" v-model="form.request_date" :disabled-date="disabledDate" />
        <span v-else>{{
          form.request_date.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })
        }}</span>
      </el-form-item>
      <el-form-item label="Miners to repair" prop="expected_units">
        <el-input-number v-model="form.expected_units" :min="1" placeholder="0" controls-position="right" />
      </el-form-item>
      <el-form-item label="Miner Families" prop="miner_models">
        <el-select-v2
          v-if="minerModelFamilyOptions"
          v-model="form.miner_models"
          :options="minerModelFamilyOptions"
          multiple
          filterable
          placeholder="Select miner models..."
          clearable
        />
      </el-form-item>
      <el-form-item label="Scope of work" prop="scope_of_work">
        <div class="w-full flex flex-col gap-4">
          <el-form-item v-for="(svc, index) in form.scope_of_work" :key="svc.key">
            <div class="custom-fee-group">
              <el-form-item
                :key="`${svc.key}.scope_of_work_service`"
                :label="index === 0 ? 'Service' : ''"
                label-position="top"
                class="custom-fee-group__service-select"
                :prop="`scope_of_work.${index}.service_id`"
                :rules="{
                  required: true,
                  message: 'Service required.',
                  trigger: 'change',
                }"
              >
                <el-select-v2
                  v-model="svc.service_id"
                  placeholder="Select a service..."
                  :options="repairServiceOptions"
                  filterable
                  clearable
                  @change="updateAvailableServiceOptions"
                  @clear="updateAvailableServiceOptions"
                />
              </el-form-item>
              <el-form-item
                :key="`${svc.key}.scope_of_work_service_included_services`"
                :label="index === 0 ? 'Included Services' : ''"
                label-position="top"
                class="custom-fee-group__service-select"
                :prop="`scope_of_work.${index}.included_service_ids`"
              >
                <el-select-v2
                  v-model="svc.included_service_ids"
                  placeholder="Included services"
                  :options="staticRepairServiceOptions.filter((s) => s.value !== svc.service_id)"
                  multiple
                  filterable
                  clearable
                />
              </el-form-item>
              <el-form-item
                :key="`${svc.key}.service_price`"
                :label="index === 0 ? 'Price' : ''"
                label-position="top"
                class="custom-fee-group__service-price-input"
                label-width="0px"
                :prop="`scope_of_work.${index}.price`"
                :rules="{ required: true, message: 'Price required.', trigger: 'blur' }"
              >
                <div class="w-full flex flex-row gap-4">
                  <el-currency-input v-model="svc.price" :min-value="0" clearable />
                  <div v-if="form.scope_of_work.length >= 1" class="delete-wrapper">
                    <el-button link :icon="Delete" @click="removeService(svc)" />
                  </div>
                </div>
              </el-form-item>
            </div>
          </el-form-item>
          <el-button class="self-start" link type="primary" @click="addService">+ Add a service</el-button>
        </div>
      </el-form-item>
      <el-form-item label="Custom repair fees" style="margin-bottom: 0">
        <el-switch v-model="customRepairFees" />
      </el-form-item>
      <el-collapse-transition>
        <div v-show="customRepairFees">
          <el-space>
            <el-text size="small">Specify custom fee per repair action</el-text>
          </el-space>
          <el-form-item v-for="(fee, index) in form.repair_fees" :key="fee.key">
            <div class="custom-fee-group">
              <el-form-item
                :key="`${fee.key}.fee_repair_action`"
                class="custom-fee-group__repair-action-select"
                :prop="`repair_fees.${index}.repair_action_id`"
                :rules="{
                  required: customRepairFees,
                  message: 'Repair action required.',
                  trigger: 'change',
                }"
              >
                <el-select-v2
                  v-model="fee.repair_action_id"
                  placeholder="Select a repair action..."
                  :options="repairActionOptions"
                  filterable
                  clearable
                  @change="updateAvailableRepairFeeOptions"
                  @clear="updateAvailableRepairFeeOptions"
                />
              </el-form-item>
              <el-form-item
                :key="`${fee.key}.fee_price`"
                class="custom-fee-group__fee-price-input"
                label-width="0px"
                :prop="`repair_fees.${index}.price`"
                :rules="{ required: customRepairFees, message: 'Price required.', trigger: 'blur' }"
              >
                <el-currency-input v-model="fee.price" :min-value="0" clearable />
              </el-form-item>
              <div v-if="form.repair_fees.length > 1" class="delete-wrapper">
                <el-button link :icon="Delete" @click="removeCustomRepairFee(fee)" />
              </div>
            </div>
          </el-form-item>
          <el-button class="add-fee-button" link type="primary" @click="addCustomRepairFee"
            >+ Add a custom fee</el-button
          >
        </div>
      </el-collapse-transition>
      <el-form-item style="margin-top: 18px" label="Repair Service Document" prop="signed_documents">
        <ul v-if="repairRequestId" class="files" style="width: 100%">
          <li v-for="document in initialFiles" :key="document.id">
            <RepairRequestAttachment :rr-id="repairRequestId" :attachment="document" />
            <el-button style="width: 20px" link @click="markRSRForDeletion(document.id)">
              <Icon icon="material-symbols:delete-outline" width="20" />
            </el-button>
          </li>
        </ul>
        <el-button
          v-if="repairRequestId && !addingNewRSR"
          style="height: 32px"
          link
          type="primary"
          @click="addingNewRSR = true"
          >+ Add new documents to this request</el-button
        >
        <el-button
          v-if="repairRequestId && addingNewRSR"
          style="height: 32px"
          link
          type="danger"
          @click="handleCancelAddingNewDocuments"
        >
          Cancel
        </el-button>
        <rl-upload-pdf
          v-if="!repairRequestId || (repairRequestId && addingNewRSR)"
          ref="uploadRef"
          v-model="form.signed_document"
          :max-size-mb="1"
        />
      </el-form-item>
    </el-form>
    <transition name="el-zoom-in-center">
      <el-alert v-if="alert" type="error" :title="alert?.title">
        <ul>
          <li v-for="detail in alert?.details" :key="detail">{{ detail }}</li>
        </ul>
      </el-alert>
    </transition>
    <template #footer>
      <el-button @click="visible = false">Cancel</el-button>
      <el-button type="primary" :loading="formSubmitLoading" @click="submitForm(formRef)">{{
        repairRequestId ? 'Update' : 'Create'
      }}</el-button>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.flex-col {
  display: flex;
  flex-direction: column;
}

.el-space {
  margin-left: 192px;
  margin-top: 16px;
  margin-bottom: 16px;
}

::v-deep(.el-date-editor),
::v-deep(.el-input-number) {
  width: 160px;
}

::v-deep(.el-upload-list__item) {
  background-color: var(--surface-tertiary);
}

.add-fee-button {
  margin-left: 192px;
}

.custom-fee-group {
  width: 100%;
  display: inline-flex;
  gap: 8px;

  &__repair-action-select {
    flex-grow: 1;
  }

  &__service-select {
    flex-grow: 1;
  }

  &__nested-service-select {
    flex-grow: 1;
  }

  &__service-price-input {
    width: 360px;
  }

  &__fee-price-input {
    width: 176px;
  }
}

.delete-wrapper {
  display: inline-flex;
  justify-content: center;
  align-items: center;
}

li {
  display: flex;
  gap: 8px;
}

li:last-child {
  margin-bottom: 16px;
}

.files {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
